import { DEFAULT_MULTIVALUES } from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/ActiveForm/constants";

import { DEFAULT_PHONE_PREFIX } from "web/utils/page/product/subscription/forms/getPhonePrefixOptions";

import {
  IActiveFormValues,
  ISubscriptionFormData,
  ISubscriptionReceiverFormField,
  PhoneWithPrefixValue,
  SubscriptionActiveFormFieldType,
} from "web/types/Subscription";

const typesWithValidations = [
  SubscriptionActiveFormFieldType.REQUIRE_PESEL,
  SubscriptionActiveFormFieldType.PESEL,
  SubscriptionActiveFormFieldType.EMAIL,
  SubscriptionActiveFormFieldType.PHONE,
  SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX,
  SubscriptionActiveFormFieldType.POSTCODE,
];

/**
 * Function parsing active form data from API to structure used for by Formik. Returns object with values and array of validation rules.
 * @param { array } fields - fields configuration from API.
 * @param { object } formData - formData - form fields in interal state (includes currently changed values).
 */
export default (
  fields: ISubscriptionReceiverFormField[],
  formData: ISubscriptionFormData,
  lang: { key: string; code: string }
) => {
  const init: IActiveFormValues = {};
  const toValidate: any[] = [];

  fields.forEach((field) => {
    const rules: (string | { name: string; additional: unknown })[] = [];
    const { id, type, required, requiredWhenFilled, notRequiredWhenFilled } =
      field;

    switch (type) {
      case SubscriptionActiveFormFieldType.CHECKBOX:
        init[id] = !!formData.fields[id];
        break;
      case SubscriptionActiveFormFieldType.REQUIRE_PESEL:
        init[id] = formData.fields[id]
          ? formData.fields[id]
          : DEFAULT_MULTIVALUES[SubscriptionActiveFormFieldType.REQUIRE_PESEL];
        break;
      case SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX: {
        const phoneData = formData.fields[id] as
          | PhoneWithPrefixValue
          | undefined;
        if (phoneData) {
          init[id] = {
            ...phoneData,
            prefix: phoneData.prefix || DEFAULT_PHONE_PREFIX,
          };
        } else {
          init[id] =
            DEFAULT_MULTIVALUES[
              SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX
            ];
        }
        break;
      }
      default:
        init[id] = formData.fields[id] || "";
    }

    if (required && !notRequiredWhenFilled?.length) {
      // For phoneWithPrefix, don't add generic 'required' rule - it's handled inside the type validator
      if (type !== SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX) {
        rules.push("required");
      }
    }

    if (requiredWhenFilled) {
      // For phoneWithPrefix, requiredWhenFilled is passed through additional to its own validator
      if (type !== SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX) {
        if (notRequiredWhenFilled?.length) {
          rules.push({
            name: "requiredWhenFilled",
            additional: { fieldId: requiredWhenFilled, notRequiredWhenFilled },
          });
        } else {
          rules.push({
            name: "requiredWhenFilled",
            additional: requiredWhenFilled,
          });
        }
      }
    }

    if (notRequiredWhenFilled?.length) {
      // For phoneWithPrefix, notRequiredWhenFilled is passed through additional to its own validator
      if (type !== SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX) {
        if (!requiredWhenFilled) {
          rules.push({
            name: "notRequiredWhenFilled",
            additional: notRequiredWhenFilled,
          });
        }
      }
    }

    if (
      typesWithValidations.includes(type as SubscriptionActiveFormFieldType)
    ) {
      // For phoneWithPrefix, pass required info through ruleAdditional
      if (type === SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX) {
        rules.push({
          name: type,
          additional: { required, requiredWhenFilled, notRequiredWhenFilled },
        });
      } else {
        rules.push(type);
      }
    }

    if (type === SubscriptionActiveFormFieldType.NUMBER) {
      rules.push("numeric");
    }

    toValidate.push({
      name: id,
      rules,
      ignoreSubResultValidation:
        type === SubscriptionActiveFormFieldType.REQUIRE_PESEL ||
        type === SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX,
      selectedLanguage: lang.key,
    });
  });

  // a little hack to fix formik - it sets meta.touched to false if there is >1 nested fields (requirePesel)
  const initJSON = JSON.stringify(init);
  return [JSON.parse(initJSON), toValidate];
};
